import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLanguageCode } from '../../../utils/language';

const SCRIPT_SRC = 'https://widget.reviews.io/carousel-inline-iframeless/dist.js';

const CSS_1 = 'https://assets.reviews.io/css/widgets/carousel-widget.css?_t=2024062614';
const CSS_2 = 'https://assets.reviews.io/iconfont/reviewsio-icons/style.css?_t=2024062614';

let widgetScriptPromise;

const loadWidgetScript = () => {
  if (window.carouselInlineWidget) {
    return Promise.resolve();
  }

  if (widgetScriptPromise) {
    return widgetScriptPromise;
  }

  widgetScriptPromise = new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);

    const handleLoad = () => resolve();
    const handleError = () => {
      widgetScriptPromise = undefined;
      reject(new Error('Unable to load the Reviews.io widget script.'));
    };

    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
  });

  return widgetScriptPromise;
};

export const WIDGET_DATA = {
  store: 'weriise.co',
  sku: '',
  lang: 'en',
  carousel_type: 'topHeader',
  styles_carousel: 'CarouselWidget--topHeader--withcards',
  options: {
    general: {
      review_type: 'company',
      min_review_percent: 100,
      // min_reviews: '1',
      // max_reviews: '20',
      address_format: 'CITY, COUNTRY',
      enable_auto_scroll: 10000,
    },
    header: {
      enable_overall_stars: true,
      rating_decimal_places: 2,
    },
    reviews: {
      enable_customer_name: true,
      enable_customer_location: true,
      enable_verified_badge: true,
      enable_subscriber_badge: true,
      enable_recommends_badge: true,
      enable_photos: true,
      enable_videos: true,
      enable_review_date: true,
      disable_same_customer: true,
      min_review_percent: 5,
      third_party_source: true,
      hide_empty_reviews: true,
      enable_product_name: true,
      tags: '',
      branch: '',
      enable_branch_name: false,
    },
    popups: {
      enable_review_popups: true,
      enable_helpful_buttons: true,
      enable_helpful_count: true,
      enable_share_buttons: true,
    },
  },
  translations: {
    verified_customer: 'Verified Customer',
  },
  styles: {
    '--base-font-size': '16px',
    '--base-maxwidth': '100%',
    '--star-color': '#F6E147',
    '--header-star-color': '#F6E147',
    '--rating-star-color': '#F6E147',
  },
};

const ReviewsMobile = ({ sku = '', reviewType = 'company' }) => {
  const { i18n, t } = useTranslation();
  const containerRef = useRef(null);
  const selectedLang = normalizeLanguageCode(i18n.language);

  useEffect(() => {
    let isCurrent = true;
    const container = containerRef.current;

    // ensure CSS
    if (!document.querySelector(`link[href="${CSS_1}"]`)) {
      const l1 = document.createElement('link');
      l1.rel = 'stylesheet';
      l1.href = CSS_1;
      document.head.appendChild(l1);

      const l2 = document.createElement('link');
      l2.rel = 'stylesheet';
      l2.href = CSS_2;
      document.head.appendChild(l2);
    }

    // star color + font override
    if (!document.querySelector('#reviewsio-star-override')) {
      const style = document.createElement('style');
      style.id = 'reviewsio-star-override';
      style.textContent = `
        #reviewsio-carousel-widget-mobile .ricon-star,
        #reviewsio-carousel-widget-mobile .CarouselWidget__starRating__fillIcon,
        #reviewsio-carousel-widget-mobile [class*="star"] { color: #F6E147 !important; }
        #reviewsio-carousel-widget-mobile *:not([class*="ricon"]):not([class*="Icon"]):not(i) { font-family: 'Plus Jakarta Sans', sans-serif !important; }
      `;
      document.head.appendChild(style);
    }

    const initWidget = () => {
      if (window.carouselInlineWidget && container) {
        container.innerHTML = ''; // force fresh render
        new window.carouselInlineWidget(container.id, {
          ...WIDGET_DATA,
          sku,
          lang: selectedLang,
          translations: {
            ...WIDGET_DATA.translations,
            verified_customer: t('Omega3_Reviews_VerifiedCustomer'),
          },
          options: {
            ...WIDGET_DATA.options,
            general: {
              ...WIDGET_DATA.options.general,
              review_type: reviewType,
            },
          },
        });
      }
    };

    loadWidgetScript()
      .then(() => {
        if (isCurrent) initWidget();
      })
      .catch(() => {
        // Keep the page usable when the third-party widget cannot be loaded.
      });

    return () => {
      isCurrent = false;
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [reviewType, selectedLang, sku, t]);

  return <div id="reviewsio-carousel-widget-mobile" ref={containerRef} />;
};

export default ReviewsMobile;
